import React, { FC, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import _debug from "debug"

import { displayError } from '../utils/Errors'
import { fmt, s } from '../utils/Fmt'
import { AppRoot } from '../../models3/AppRoot'
import { t } from 'ttag'
import { decode} from 'jsonwebtoken-esm'
import { useAuth0 } from "@auth0/auth0-react"
import API from '../../models3/API'
import { getRedirectUri } from '../utils/Auth0Utils'


const log = _debug('sltt:Login')

const SECONDS_TO_EXPIRATION_WHEN_TESTING = 45
const REQUIRED_IDLE_SECONDS = localStorage.getItem('forceLogoutOnIdTokenExpiration') ? SECONDS_TO_EXPIRATION_WHEN_TESTING : 15 * 60

// To see detailed log message for just this module
// set: localStorage.debug='slttdbg:Login'
const dbg = _debug('slttdbg:Login')

export function parseJwt(token: string) {
    let decoded: any = decode(token)
    decoded._expiryDays = (decoded.exp - decoded.iat) / (24 * 3600)

    return decoded
}

/**
 * When id_token nears expiration and system is idle, sign out user.
 */
interface IIsRecording {
    recording: boolean
    resumableRecording: boolean
}
interface ILogoutOnIdTokenExpiration { 
    appRoot: AppRoot 
    isRecording?: IIsRecording
}

export const LogoutOnIdTokenExpiration: FC<ILogoutOnIdTokenExpiration> = observer(({ appRoot, isRecording }) => {
    // Requried idle time before expired token forces logout

    const { id_token } = appRoot

    // These are observable values which cause component re-renders
    const { recording, resumableRecording } = isRecording || { recording: false, resumableRecording: false }

    const idleStartRef = useRef(Date.now())

    const [idTokenExpiring, setIdTokenExpiring] = useState(false)

    const signOut = useSignOut(appRoot)

    // setIdTokenExpiring(true) when token is 75% expired
    useEffect(() => {
        if (id_token === '') return

        const decoded = parseJwt(id_token)
        const totalExpirationSecondsForToken = decoded.exp - decoded.iat
        // current app has 8 days total expiration time, so 25% is 2 days 
        const twentyFivePercentOfExpirationInSeconds = totalExpirationSecondsForToken * 0.25 
        let secondsToExpiration = (decoded.exp - Date.now()/1000) 
        
        // Force token to expire for testing purposes
        if (localStorage.getItem('forceLogoutOnIdTokenExpiration')) {
            secondsToExpiration = SECONDS_TO_EXPIRATION_WHEN_TESTING // force auto logout, for testing
        }
        log('LogoutOnIdTokenExpiration', fmt({ secondsToExpiration, daysToExpiration: secondsToExpiration / (24 * 3600) }))

        // Start logout process two days before expiration.
        // Logout will happen when there is idle time.
        // NOTE: according to chatGPT, when the computer goes to sleep, it delays the timer by the 
        // amount of time that it was asleep
        // as a result, we also need to handle "TokenExpiredError: jwt expired" errors from 
        // from fetch calls to the API server (e.g. via API.retriableFetch)
        const timer = setTimeout(() => {
            console.warn('id_token expiring soon, starting idle timeout...')
            setIdTokenExpiring(true)
        }, 1000 * (secondsToExpiration - twentyFivePercentOfExpirationInSeconds))
        return () => clearTimeout(timer)
    }, [id_token])

    const recordActivity = () => { 
        idleStartRef.current = Date.now() // reset idle timer initial time
    }

    // Reset idle start time (idleStartRef.current) on mouse or keyboard events
    useEffect(() => {
        if (!idTokenExpiring) return
        
        recordActivity() // initialize idle start time

        document.addEventListener("mousemove", recordActivity)
        document.addEventListener("keydown", recordActivity)
        return function cleanup() {
            document.removeEventListener("mousemove", recordActivity)
            document.removeEventListener("keydown", recordActivity)
        }
    }, [idTokenExpiring])

    // Periodically check idTokenExpiring. When true, wait for inactivity and then force logout
    useEffect(() => {
        const interval = setInterval(async () => {
            if (!idTokenExpiring) return

            log('idTokenExpiring === true')

            // If we can't reach the SLTT server, assume we are offline and don't force logout
            if (!(await API.serverIsReachable())) return

            if (recording || resumableRecording) {
                log('delay logout while recording')
                idleStartRef.current = Date.now() // reset idle timer initial time
            }

            // require 5 minutes of inactivity before forcing logout
            const idleSeconds = (Date.now() - idleStartRef.current) / 1000
            
            if (idleSeconds < REQUIRED_IDLE_SECONDS) {
                log('seconds until forced logout', REQUIRED_IDLE_SECONDS - idleSeconds)
                return
            }

            log('forcing logout after inactivity of', idleSeconds, 'seconds')
            setIdTokenExpiring(false)
            signOut()
        }, 5000)
        return () => clearInterval(interval)
    }, [idTokenExpiring, recording, resumableRecording])

    return null
})

function useSignOut(appRoot: AppRoot) {
    const { logout } = useAuth0()

    function signOut() {
        for (let rt of appRoot.rts) {
            rt.project?.db?.disconnect()
        }

        // Note: we don't clear appRoot.id_token because that causes
        // reactions in other components which makes the logout process
        // erratic.
        
        localStorage.setItem('sltt_id_token', '')
        localStorage.setItem('sltt_username', '')

        logout({ returnTo: getRedirectUri() }) 
        // Logout redirects and so never gets here
    }

    return signOut
}

interface ILogoutButton { appRoot: AppRoot }

export const LogoutButton: FC<ILogoutButton> = ({ appRoot }) => {
    const { username } = appRoot

    const signOut = useSignOut(appRoot)

    useEffect(() => {
        // The API module reset the id_token when it determines that the user has been removed from the project
        // has had their email address changed to a new address.
        // When this happens we automatically sign out the user.
        const interval = setInterval(() => {
            if (!API.id_token) {
                console.warn('id_token cleared. sign out user')
                signOut()
            }
        }, 5000)
        return () => clearInterval(interval)
    }, [])

    return (
        <button className="sign-out-icon icon-hover" onClick={() => {
            console.log('LogoutButton clicked')
            signOut()
        }}>
            <span
                className={`fa fa-sign-out-alt`}
                data-id="logout-button"
                data-toggle="tooltip"
                title={t`Sign out` + `: ${username}`} />
        </button>
    )
}



